import { getGeeTestCaptcha } from '@/lib/api/geetestApi'
import { GeetestKeyEnum } from '@/typings/domain'
import { GeetestCaptchaResult, startCaptcha } from '@modules/geetest-module'
import * as Sentry from '@sentry/react-native'
import { useCallback, useEffect, useRef } from 'react'
import { Platform } from 'react-native'

export type GeetestResultData = Pick<GeetestCaptchaResult, 'pass_token' | 'lot_number' | 'gen_time' | 'captcha_output'> & {
  type: GeetestKeyEnum
}

export type GeetestType = 'sms'

const geetestTypeMap = Platform.select<Record<GeetestType, GeetestKeyEnum>>({
  ios: { sms: GeetestKeyEnum.SmsIos },
  android: { sms: GeetestKeyEnum.SmsAndroid },
  web: { sms: GeetestKeyEnum.SendSmsV4 },
})

type GeeTestWrapperProps = {
  type: GeetestType
  onSuccess?: (result: GeetestResultData) => void
  /** Only fires when the captcha could not be started at all, never when the user's attempt fails */
  onError?: (error: string | undefined) => void
  children: (trigger: () => Promise<GeetestResultData | null>) => React.ReactNode
}

export default function GeeTest({ type, onSuccess, onError, children }: GeeTestWrapperProps) {
  const callbackRef = useRef({ onSuccess, onError })
  const promiseRef = useRef<{ resolve: (result: GeetestResultData | null) => void } | undefined>(undefined)
  callbackRef.current = { onSuccess, onError }
  const hasPending = useRef(false)
  const captchaIdRef = useRef<{ type: GeetestKeyEnum; id: string } | null>(null)

  // trigger's promise goes straight into a button's onPress, so a rejection would never be
  // observed and would reach Sentry as an unhandled rejection. Only ever resolve.
  const settle = useCallback((result: GeetestResultData | null) => {
    promiseRef.current?.resolve(result)
    promiseRef.current = undefined
  }, [])

  const showCaptcha = useCallback(async () => {
    try {
      if (!captchaIdRef.current) throw new Error('Captcha ID is not set')

      const result = await startCaptcha(captchaIdRef.current.id)
      // Cancelled, network, risk control — all the user's own attempt, and only a platform-specific
      // code that shifts between SDK versions could tell them apart. Stay silent rather than risk
      // alerting someone who just closed the popup.
      if (!result.success) {
        settle(null)
        return
      }

      const geetest4Data: GeetestResultData = {
        captcha_output: result.result.captcha_output,
        gen_time: result.result.gen_time,
        lot_number: result.result.lot_number,
        pass_token: result.result.pass_token,
        type: captchaIdRef.current.type,
      }

      callbackRef.current.onSuccess?.(geetest4Data)
      settle(geetest4Data)
    } catch (error) {
      // Unlike a non-success result, a throw means the captcha never ran — our bug, worth reporting
      Sentry.captureException(error)
      callbackRef.current.onError?.(error instanceof Error ? error.message : undefined)
      settle(null)
    }
  }, [settle])

  const initCaptcha = useCallback(async () => {
    captchaIdRef.current = null

    try {
      const geetestType = geetestTypeMap?.[type]
      if (!geetestType) throw new Error(`Invalid geetest type: ${type}`)

      const res = await getGeeTestCaptcha({ type: geetestType })
      const captchaId = res.data?.initCaptcha?.geetestId
      if (!captchaId) throw new Error('Captcha init returned no geetestId')

      captchaIdRef.current = { id: captchaId, type: geetestType }
      if (hasPending.current) {
        hasPending.current = false
        showCaptcha()
      }
    } catch (error) {
      Sentry.captureException(error)
      // Only surface this once the user has tapped; a failure on mount should not interrupt them
      if (hasPending.current) {
        hasPending.current = false
        callbackRef.current.onError?.(error instanceof Error ? error.message : undefined)
        settle(null)
      }
    }
  }, [type, showCaptcha, settle])

  useEffect(() => {
    initCaptcha()
  }, [initCaptcha])

  const trigger = useCallback(() => {
    const promise = new Promise<GeetestResultData | null>(resolve => {
      promiseRef.current = { resolve }
    })

    if (captchaIdRef.current) showCaptcha()
    else hasPending.current = true

    return promise
  }, [showCaptcha])

  return children(trigger)
}
